import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import {
  FRAMEWORKS,
  type FrameworkId,
  type DeliverableType,
  type DeliverableExportStatus,
} from '@seedgovernance/shared';
import { useWorkspace } from '../../hooks/useWorkspace';
import { apiClient } from '../../lib/api';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';

interface DeliverableField {
  id: string;
  label: string;
  value: string;
  required: boolean;
}

interface DeliverableDetail {
  id: string;
  name: string;
  frameworkId: FrameworkId;
  type: DeliverableType;
  completedFields: number;
  totalFields: number;
  exportStatus: DeliverableExportStatus;
  fields: DeliverableField[];
}

export function DeliverableDetail() {
  const { deliverableId } = useParams<{ deliverableId: string }>();
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const [deliverable, setDeliverable] = useState<DeliverableDetail | null>(null);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [savingField, setSavingField] = useState<string | null>(null);
  const [savingAll, setSavingAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportResult, setExportResult] = useState<{ downloadUrl: string; filename: string } | null>(null);

  useEffect(() => {
    if (!currentWorkspace || !deliverableId) return;
    fetchDeliverable();
  }, [currentWorkspace, deliverableId]);

  async function fetchDeliverable() {
    if (!currentWorkspace || !deliverableId) return;
    setLoading(true);
    setError(null);

    const { data, error: apiError } = await apiClient.get<DeliverableDetail>(
      `/api/tenants/${currentWorkspace.tenantId}/deliverables/${deliverableId}`
    );

    if (apiError) {
      setError(apiError.message);
    } else if (data) {
      setDeliverable(data);
      const values: Record<string, string> = {};
      for (const field of data.fields) {
        values[field.id] = field.value;
      }
      setFieldValues(values);
    }
    setLoading(false);
  }

  async function saveField(fieldId: string) {
    if (!currentWorkspace || !deliverableId) return;
    setSavingField(fieldId);

    const { error: apiError } = await apiClient.patch(
      `/api/tenants/${currentWorkspace.tenantId}/deliverables/${deliverableId}/fields/${fieldId}`,
      { value: fieldValues[fieldId] }
    );

    if (apiError) {
      setError(apiError.message);
    } else {
      // Update local state to reflect saved value
      setDeliverable((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          fields: prev.fields.map((f) =>
            f.id === fieldId ? { ...f, value: fieldValues[fieldId] } : f
          ),
        };
      });
    }
    setSavingField(null);
  }

  async function saveAllFields() {
    if (!currentWorkspace || !deliverableId) return;
    setSavingAll(true);
    setError(null);

    const { error: apiError } = await apiClient.patch(
      `/api/tenants/${currentWorkspace.tenantId}/deliverables/${deliverableId}/fields`,
      { fields: fieldValues }
    );

    if (apiError) {
      setError(apiError.message);
    } else {
      setDeliverable((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          fields: prev.fields.map((f) => ({
            ...f,
            value: fieldValues[f.id] ?? f.value,
          })),
        };
      });
    }
    setSavingAll(false);
  }

  async function handleExport() {
    if (!currentWorkspace || !deliverableId) return;
    setExporting(true);
    setError(null);
    setExportResult(null);

    const { data, error: apiError } = await apiClient.post<{
      downloadUrl: string;
      filename: string;
    }>(
      `/api/tenants/${currentWorkspace.tenantId}/deliverables/${deliverableId}/export`,
      { format: 'markdown' }
    );

    if (apiError) {
      setError(apiError.message);
    } else if (data) {
      setExportResult(data);
    }
    setExporting(false);
  }

  const typeBadgeVariant = (type: DeliverableType) => {
    switch (type) {
      case 'policy': return 'brand' as const;
      case 'process': return 'info' as const;
      case 'register': return 'warning' as const;
      case 'evidence': return 'success' as const;
      default: return 'default' as const;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <svg className="w-8 h-8 animate-spin text-brand" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  if (!deliverable) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="text-center">
          <h1 className="text-2xl font-display font-bold text-white mb-2">Deliverable Not Found</h1>
          <p className="text-slate-400 mb-4">This deliverable does not exist or you do not have access.</p>
          <Button variant="secondary" onClick={() => navigate('/deliverables')}>
            Back to Deliverables
          </Button>
        </div>
      </div>
    );
  }

  const framework = FRAMEWORKS[deliverable.frameworkId];
  const completedCount = deliverable.fields.filter((f) => (fieldValues[f.id] || '').trim().length > 0).length;
  const totalCount = deliverable.fields.length;
  const completionPct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  const exportStatusBanner = () => {
    switch (deliverable.exportStatus) {
      case 'locked':
        return (
          <div className="bg-surface-mid rounded-xl border border-border p-4 flex items-center gap-3">
            <svg className="w-5 h-5 text-slate-500 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0110 0v4" />
            </svg>
            <div>
              <p className="text-sm font-medium text-slate-300">Locked</p>
              <p className="text-xs text-slate-500">
                Complete the framework assessment and fill in required fields to unlock this deliverable for export.
              </p>
            </div>
          </div>
        );
      case 'unlockable':
        return (
          <div className="bg-yellow-500/5 rounded-xl border border-yellow-500/20 p-4 flex items-center gap-3">
            <svg className="w-5 h-5 text-yellow-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 019.9-1" />
            </svg>
            <div>
              <p className="text-sm font-medium text-yellow-400">Almost Ready</p>
              <p className="text-xs text-slate-500">
                Continue filling in the required fields below to make this deliverable exportable.
              </p>
            </div>
          </div>
        );
      case 'exportable':
        return (
          <div className="bg-growth/5 rounded-xl border border-growth/20 p-4 flex items-center gap-3">
            <svg className="w-5 h-5 text-growth shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20,6 9,17 4,12" />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-medium text-growth">Ready to Export</p>
              <p className="text-xs text-slate-500">
                All required fields are complete. This deliverable is ready for export.
              </p>
            </div>
            <Button size="sm" className="shrink-0" loading={exporting} onClick={handleExport}>
              Export
            </Button>
          </div>
        );
    }
  };

  const exportDialog = exportResult && (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-surface-mid rounded-xl border border-border p-6 max-w-md w-full mx-4 space-y-4">
        <div className="flex items-center gap-3">
          <svg className="w-6 h-6 text-growth" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="20,6 9,17 4,12" />
          </svg>
          <h3 className="text-lg font-display font-semibold text-white">Export Ready</h3>
        </div>
        <p className="text-sm text-slate-400">
          Your deliverable has been exported as <span className="text-white font-medium">{exportResult.filename}</span>.
          The download link is valid for 60 minutes.
        </p>
        <div className="flex gap-3">
          <a
            href={exportResult.downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-brand hover:bg-brand-dark text-white px-4 py-2 text-sm flex-1"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="7,10 12,15 17,10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Download
          </a>
          <Button variant="secondary" onClick={() => setExportResult(null)}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {exportDialog}

      {/* Back link */}
      <Link to="/deliverables" className="text-sm text-slate-400 hover:text-white transition-colors inline-flex items-center gap-1">
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="15,18 9,12 15,6" />
        </svg>
        Back to Deliverables
      </Link>

      {/* Header */}
      <div>
        <h1 className="text-2xl font-display font-bold text-white">{deliverable.name}</h1>
        <div className="flex items-center gap-2 mt-2">
          <Badge variant={typeBadgeVariant(deliverable.type)}>{deliverable.type}</Badge>
          {framework && (
            <span className="inline-flex items-center gap-1 text-xs text-slate-400">
              <span
                className="w-2 h-2 rounded-full inline-block"
                style={{ backgroundColor: framework.color }}
              />
              {framework.shortName}
            </span>
          )}
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {/* Export status banner */}
      {exportStatusBanner()}

      {/* Completion progress */}
      <div className="bg-surface-mid rounded-xl border border-border p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-slate-300">Completion</span>
          <span className="text-sm font-medium text-white">{completionPct}%</span>
        </div>
        <div className="h-2 bg-surface-dark rounded-full overflow-hidden">
          <div
            className="h-full bg-brand rounded-full transition-all"
            style={{ width: `${completionPct}%` }}
          />
        </div>
        <p className="text-xs text-slate-500 mt-2">
          {completedCount} of {totalCount} fields completed
        </p>
      </div>

      {/* Field editor */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-display font-semibold text-white">Fields</h2>
          <Button
            variant="secondary"
            size="sm"
            loading={savingAll}
            onClick={saveAllFields}
          >
            Save All
          </Button>
        </div>

        {deliverable.fields.length === 0 ? (
          <Card>
            <div className="text-center py-8">
              <p className="text-slate-400 text-sm">No fields defined for this deliverable.</p>
            </div>
          </Card>
        ) : (
          deliverable.fields.map((field) => (
            <Card key={field.id}>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-slate-300">
                    {field.label}
                    {field.required && (
                      <span className="text-red-400 ml-1">*</span>
                    )}
                  </label>
                  <Button
                    variant="ghost"
                    size="sm"
                    loading={savingField === field.id}
                    onClick={() => saveField(field.id)}
                    disabled={fieldValues[field.id] === field.value}
                  >
                    Save
                  </Button>
                </div>
                <textarea
                  value={fieldValues[field.id] || ''}
                  onChange={(e) =>
                    setFieldValues((prev) => ({
                      ...prev,
                      [field.id]: e.target.value,
                    }))
                  }
                  rows={4}
                  className="w-full px-3 py-2 bg-surface-dark border border-border rounded-lg text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors resize-y"
                  placeholder={`Enter ${field.label.toLowerCase()}...`}
                />
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
